import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import Dropdown from '../components/Dropdown';
import Button from '../../../react/components/Button';
import Loader from '../../../../web/images/svg/loader.svg';

import {
  useAddPerformanceMutation,
  useAddPerformanceSingleCyoMutation,
  useConcertPerformancesQuery,
} from '../redux/tessituraApiSlice';

import { getClassNames } from '../../../utils';
import { useAppSelector } from '../hooks';
import { genericErrorMessage } from '../utils';

const AddToPack = ({
  handleCloseModal, parentRef, productionSeasonId,
}: AddToPackProps) => {
  const {
    data,
    error,
    isFetching: isFetchingPerformances,
  } = useConcertPerformancesQuery(productionSeasonId, {
    refetchOnMountOrArgChange: true,
  });

  const [
    addPerformance,
    {
      isLoading: isAddingPerformance,
      error: errorAddingPerformance,
      isSuccess: successAddingPerformance,
    },
  ] = useAddPerformanceMutation();

  const [
    addPerformanceSingleCyo,
    {
      isLoading: isAddingSingleCyoPerformance,
      error: errorAddingSingleCyoPerformance,
      isSuccess: successAddingSingleCyoPerformance,
    },
  ] = useAddPerformanceSingleCyoMutation();

  const {
    cyoType, packageLineNo,
  } = useAppSelector((state) => state.sessionSlice);

  const [buttonNextEnabled, setButtonNextEnabled] = useState(false);
  const [closeCurrentModal, setCloseCurrentModal] = useState(false);
  const [selectedPerformanceId, setSelectedPerformanceId] = useState(0);
  const [selectedPerformanceLabel, setSelectedPerformanceLabel] = useState('-');
  const [selectedPerformanceMapImage, setSelectedPerformanceMapImage] = useState('');
  const [selectedPerformanceZoneId, setSelectedPerformanceZoneId] = useState(0);

  const formatPerformanceDate = (dateString: string) => dayjs(dateString).format('DD MMM YYYY, h:mma');
  const getSelectedPerformance = (id: string, selectedPerformances: DetailedPerformance[]) => (
    selectedPerformances.find(({ performanceId }) => performanceId === parseInt(id, 10))
  );

  useEffect(() => {
    if (successAddingPerformance || successAddingSingleCyoPerformance) setCloseCurrentModal(true);
  }, [successAddingPerformance, successAddingSingleCyoPerformance]);

  useEffect(() => {
    if (closeCurrentModal && !isAddingPerformance && !isAddingSingleCyoPerformance) {
      handleCloseModal();
      setCloseCurrentModal(true);
    }
  }, [closeCurrentModal, handleCloseModal, isAddingPerformance, isAddingSingleCyoPerformance]);

  useEffect(() => {
    if ((errorAddingPerformance || errorAddingSingleCyoPerformance) && 'current' in parentRef) {
      const modal = parentRef.current;
      if (modal) modal.scrollBy(0, modal.scrollHeight);
    }
  }, [errorAddingPerformance, errorAddingSingleCyoPerformance, parentRef]);

  if (isFetchingPerformances) return <div className="c-loader c-loader--cyop"><Loader /></div>;
  if (!data && !error) return <div>Sorry, no performances available.</div>;
  if (error) return <div>{`Error: ${error}`}</div>;
  if (!data) return <div>Error: no data was retrieved</div>;

  const getErrorMessage = () => (
    errorAddingPerformance && 'message' in errorAddingPerformance
      ? errorAddingPerformance.message || genericErrorMessage
      : genericErrorMessage
  );

  const getErrorSingleCyoMessage = () => (
    errorAddingSingleCyoPerformance && 'message' in errorAddingSingleCyoPerformance
      ? errorAddingSingleCyoPerformance.message || genericErrorMessage
      : genericErrorMessage
  );

  const handleSelectDate = (id: string) => {
    const selectedPerformance = getSelectedPerformance(id, data.performances);

    if (selectedPerformance) {
      setSelectedPerformanceId(parseInt(id, 10));
      setSelectedPerformanceLabel(formatPerformanceDate(selectedPerformance.date));
      setSelectedPerformanceMapImage(selectedPerformance.mapImage);
    }
  };

  const handleSelectSection = (zoneId: number) => {
    setSelectedPerformanceZoneId(zoneId);
    setButtonNextEnabled(true);
  };

  const handleAddPerformance = async () => {
    try {
      await addPerformance({
        packageLineNo,
        performanceId: selectedPerformanceId,
        zoneId: selectedPerformanceZoneId,
      });
    } catch ({ addPerformanceError }) {
      throw new Error(`Error adding performance: ${addPerformanceError}`);
    }
  };

  const handleAddPerformanceSingleCyo = async () => {
    try {
      await addPerformanceSingleCyo({
        performanceId: selectedPerformanceId,
        zoneId: selectedPerformanceZoneId,
      });
    } catch ({ addPerformanceError }) {
      throw new Error(`Error adding performance: ${addPerformanceError}`);
    }
  };

  const renderZoneSelector = () => {
    const selectedPerformance = data.performances.find(
      ({ performanceId }) => performanceId === selectedPerformanceId,
    );

    const availableSections = selectedPerformance?.zones?.filter(
      ({
        notAvailableForSeatSelection, zoneId, zoneGroupTitle, zonePrice,
      }) => !notAvailableForSeatSelection && ({
        zoneGroupTitle, zoneId, zonePrice,
      }),
    );

    if (!availableSections || availableSections.length === 0) return <div>Sorry, no sections available.</div>;

    return availableSections
      .map(({ zoneGroupTitle, zoneId, zonePrice }) => (
        <div
          className={getClassNames(null, 'c-cyop__zone', {
            'c-cyop__zone--selected': zoneId === selectedPerformanceZoneId,
          })}
          key={zoneId}
          onClick={() => handleSelectSection(zoneId)}
          onKeyDown={() => handleSelectSection(zoneId)}
          role="button"
          tabIndex={0}
        >
          <span>
            {zoneGroupTitle}
            <br />
            {zonePrice}
          </span>
        </div>
      ));
  };
  return (
    <div className="c-cyop__add-to-pack">
      <div className="c-cyop__select-date">
        <span className="c-cyop__add-to-pack-label">Select performance date</span>

        <Dropdown
          id="date-selection"
          label={selectedPerformanceLabel}
          options={data.performances.map((performance) => ({
            label: formatPerformanceDate(performance.date),
            value: performance.performanceId.toString(),
          }))}
          handleDropdownSelect={(id) => handleSelectDate(id)}
        />
      </div>

      <div className="c-cyop__select-section">
        <span className={getClassNames(null, 'c-cyop__add-to-pack-label', {
          'c-cyop__add-to-pack-label--disabled': selectedPerformanceId === 0,
        })}
        >
          Select section
        </span>

        {selectedPerformanceId !== 0
          && (
            <div className="c-cyop__sections">
              {renderZoneSelector()}
            </div>
          )}

        {selectedPerformanceMapImage && (
          <div className="c-cyop__performance-image">
            <img
              alt="Seat map"
              src={selectedPerformanceMapImage}
            />
          </div>
        )}
      </div>

      <div className="c-cyop__modal-buttons">
        <Button
          cyopInverse
          cyopModal
          onClick={handleCloseModal}
          onKeyDown={handleCloseModal}
        >
          Cancel
        </Button>
        <Button
          cyopModal
          cyopModalNext
          disabled={!buttonNextEnabled || isAddingPerformance || isAddingSingleCyoPerformance}
          onClick={cyoType === 'single-cyo' ? handleAddPerformanceSingleCyo : handleAddPerformance}
          onKeyDown={cyoType === 'single-cyo' ? handleAddPerformanceSingleCyo : handleAddPerformance}
          cyopModalWithLoader={isAddingPerformance || isAddingSingleCyoPerformance}
        >
          Add To Pack
          {(isAddingPerformance || isAddingSingleCyoPerformance) && (
            <div className="c-loader c-loader--button c-loader--dark c-loader--small"><Loader /></div>
          )}
        </Button>
      </div>

      {errorAddingPerformance && <div className="c-cyop__modal-error">{getErrorMessage()}</div>}
      {errorAddingSingleCyoPerformance && <div className="c-cyop__modal-error">{getErrorSingleCyoMessage()}</div>}
    </div>
  );
};

const addToPackPropTypes = {
  handleCloseModal: PropTypes.func.isRequired,
  parentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
  productionSeasonId: PropTypes.number.isRequired,
};

AddToPack.propTypes = addToPackPropTypes;

type AddToPackProps = PropTypes.InferProps<typeof addToPackPropTypes>;

export default AddToPack;
